// yard/YardHUD.jsx - HUD overlay with controls, filters, and legend
import React, { useState } from 'react';
import useYardStore from './yardStore';

// Squirrel icon SVG
const SquirrelIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6c-1.5 0-3 1-3 2.5s1 2.5 2.5 2.5c.5 0 1-.1 1.5-.3" />
    <path d="M12 12c-2 0-4 2-4 4s2 4 4 4 4-2 4-4-2-4-4-4z" />
    <path d="M8 12c0-2-1-4-3-4" />
    <path d="M16 20c2-2 2-6 0-8" />
    <circle cx="14" cy="14" r="1" fill="currentColor" />
  </svg>
);

const YardHUD = ({ darkMode, deals = [] }) => {
  const dropSquirrel = useYardStore(state => state.dropSquirrel);
  const squirrelEvent = useYardStore(state => state.squirrelEvent);
  const dogs = useYardStore(state => state.dogs);
  const filters = useYardStore(state => state.filters);
  const setFilter = useYardStore(state => state.setFilter);
  const resetCamera = useYardStore(state => state.resetCamera);

  const [showFilters, setShowFilters] = useState(false);

  // Calculate stats
  const stats = {
    total: dogs.length,
    active: dogs.filter(d => d.dealState === 'ACTIVE').length,
    stale: dogs.filter(d => d.dealState === 'STALE').length,
    cold: dogs.filter(d => d.dealState === 'COLD').length,
  };

  // Get unique owners and stages for filters
  const owners = [...new Set(dogs.map(d => d.deal?.owner).filter(Boolean))];
  const stages = ['Prospect', 'Qualified', 'Proposal', 'Negotiation', 'Closing', 'Won'];

  const buttonBase = {
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
    fontWeight: 600,
    fontFamily: 'system-ui, -apple-system, sans-serif',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  };

  const cardStyle = {
    background: darkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    borderRadius: 16,
    padding: 16,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
  };

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: darkMode ? '#f8fafc' : '#1e293b',
    }}>
      {/* Top bar with stats */}
      <div style={{
        position: 'absolute',
        top: 16,
        left: 16,
        right: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        pointerEvents: 'auto',
      }}>
        {/* Stats cards */}
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ ...cardStyle, padding: '12px 20px' }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Dogs in Yard</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{stats.total}</div>
          </div>

          <div style={{ ...cardStyle, padding: '12px 16px', display: 'flex', gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#10b981', margin: '0 auto 4px' }} />
              <div style={{ fontSize: 18, fontWeight: 700 }}>{stats.active}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>Active</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#f59e0b', margin: '0 auto 4px' }} />
              <div style={{ fontSize: 18, fontWeight: 700 }}>{stats.stale}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>Stale</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444', margin: '0 auto 4px' }} />
              <div style={{ fontSize: 18, fontWeight: 700 }}>{stats.cold}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>Cold</div>
            </div>
          </div>
        </div>

        {/* Drop Squirrel button */}
        <button
          onClick={dropSquirrel}
          disabled={squirrelEvent.active || squirrelEvent.cooldown}
          style={{
            ...buttonBase,
            padding: '14px 28px',
            fontSize: 16,
            background: squirrelEvent.active
              ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
              : squirrelEvent.cooldown
                ? '#64748b'
                : 'linear-gradient(135deg, #10b981, #059669)',
            color: '#fff',
            boxShadow: squirrelEvent.active
              ? '0 4px 20px rgba(245, 158, 11, 0.4)'
              : '0 4px 20px rgba(16, 185, 129, 0.4)',
            transform: squirrelEvent.active ? 'scale(1.05)' : 'scale(1)',
            opacity: squirrelEvent.cooldown ? 0.6 : 1,
          }}
        >
          <SquirrelIcon />
          {squirrelEvent.active ? 'Chasing!' : squirrelEvent.cooldown ? 'Cooldown...' : 'Drop Squirrel!'}
        </button>
      </div>

      {/* Filter panel toggle */}
      <div style={{
        position: 'absolute',
        top: 100,
        right: 16,
        pointerEvents: 'auto',
      }}>
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            ...buttonBase,
            ...cardStyle,
            padding: '10px 16px',
            fontSize: 14,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Filters
        </button>

        {/* Filter dropdown */}
        {showFilters && (
          <div style={{ ...cardStyle, marginTop: 8, minWidth: 200 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>
                State
              </label>
              <select
                value={filters.state}
                onChange={(e) => setFilter('state', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                  background: darkMode ? '#1e293b' : '#fff',
                  color: darkMode ? '#f8fafc' : '#1e293b',
                  fontSize: 14,
                }}
              >
                <option value="all">All States</option>
                <option value="ACTIVE">Active</option>
                <option value="STALE">Stale</option>
                <option value="COLD">Cold</option>
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>
                Stage
              </label>
              <select
                value={filters.stage}
                onChange={(e) => setFilter('stage', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                  background: darkMode ? '#1e293b' : '#fff',
                  color: darkMode ? '#f8fafc' : '#1e293b',
                  fontSize: 14,
                }}
              >
                <option value="all">All Stages</option>
                {stages.map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
            </div>

            {owners.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>
                  Owner
                </label>
                <select
                  value={filters.owner}
                  onChange={(e) => setFilter('owner', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
                    background: darkMode ? '#1e293b' : '#fff',
                    color: darkMode ? '#f8fafc' : '#1e293b',
                    fontSize: 14,
                  }}
                >
                  <option value="all">All Owners</option>
                  {owners.map(owner => (
                    <option key={owner} value={owner}>{owner}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              onClick={() => {
                setFilter('state', 'all');
                setFilter('stage', 'all');
                setFilter('owner', 'all');
              }}
              style={{
                ...buttonBase,
                width: '100%',
                padding: '8px 12px',
                fontSize: 13,
                background: darkMode ? '#334155' : '#e2e8f0',
                color: darkMode ? '#f8fafc' : '#1e293b',
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Camera reset button */}
      <div style={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        pointerEvents: 'auto',
      }}>
        <button
          onClick={resetCamera}
          style={{
            ...buttonBase,
            ...cardStyle,
            padding: '10px 16px',
            fontSize: 14,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Reset View
        </button>
      </div>

      {/* Legend */}
      <div style={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        pointerEvents: 'auto',
      }}>
        <div style={{ ...cardStyle, padding: '12px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>
            Dog Size = Deal Value
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {['$5K', '$15K', '$30K', '$60K', '$100K', '$100K+'].map((label, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  width: 10 + i * 4,
                  height: 10 + i * 4,
                  borderRadius: '50%',
                  background: darkMode ? '#64748b' : '#94a3b8',
                  margin: '0 auto 4px',
                }} />
                <div style={{ fontSize: 9, color: '#64748b' }}>{label}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 12, fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
            Ring Color = Health
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#10b981' }} />
              <span style={{ fontSize: 11 }}>Active</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#f59e0b' }} />
              <span style={{ fontSize: 11 }}>Stale</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444' }} />
              <span style={{ fontSize: 11 }}>Cold</span>
            </div>
          </div>
        </div>
      </div>

      {/* Chase event overlay */}
      {squirrelEvent.active && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            fontSize: 48,
            fontWeight: 800,
            color: '#fff',
            textShadow: '0 4px 20px rgba(0,0,0,0.5)',
            animation: 'pulse 0.5s ease-in-out infinite',
          }}>
            CHASE!
          </div>
          <style>{`
            @keyframes pulse {
              0%, 100% { transform: scale(1); opacity: 0.9; }
              50% { transform: scale(1.1); opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default YardHUD;
